import {
  BadgeConfirmationSubtle,
  BadgeNeutralSubtle,
} from "@studio3t/generic-components";
import { useMemo } from "react";
import styled from "styled-components";
import { getIngestBaseUrl } from "@/constants";
import type { JobMetadata, TokenSummary } from "@/types/api";
import { EmptyState, Section, SectionTitle } from "./styles";

interface IngestPipelinesSectionProps {
  serviceName: string;
  port: number;
  jobs: JobMetadata[] | undefined;
  jobStatusMap: Record<string, boolean>;
  tokens: TokenSummary[] | undefined;
  routes: string[];
  onCopy: (text: string) => void;
  onJobClick: (jobName: string) => void;
}

interface RoutePipeline {
  jobName: string;
  running: boolean;
}

interface RouteEntry {
  route: string;
  apiKeyHint: string | undefined;
  pipelines: RoutePipeline[];
}

function buildTokenByRoute(
  tokens: TokenSummary[] | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  if (!tokens) return map;
  for (const t of tokens) {
    if (t.resource != null) map.set(t.resource, t.prefix);
  }
  return map;
}

function groupPipelinesByRoute(
  jobs: JobMetadata[] | undefined,
  serviceName: string,
  jobStatusMap: Record<string, boolean>,
): Map<string, RoutePipeline[]> {
  const map = new Map<string, RoutePipeline[]>();
  if (!jobs) return map;
  for (const job of jobs) {
    const source = job.pipeline?.source;
    if (!source || source.service_name !== serviceName) continue;
    const route = source.resource || "";
    const list = map.get(route) ?? [];
    list.push({ jobName: job.name, running: jobStatusMap[job.name] ?? false });
    map.set(route, list);
  }
  return map;
}

function toRouteEntry(
  route: string,
  tokenByRoute: Map<string, string>,
  pipelinesByRoute: Map<string, RoutePipeline[]>,
): RouteEntry {
  const pipelines = (pipelinesByRoute.get(route) ?? []).sort((a, b) =>
    a.jobName.localeCompare(b.jobName),
  );
  return { route, apiKeyHint: tokenByRoute.get(route), pipelines };
}

interface RouteEntriesInput {
  routes: string[];
  jobs: JobMetadata[] | undefined;
  serviceName: string;
  jobStatusMap: Record<string, boolean>;
  tokens: TokenSummary[] | undefined;
}

function buildRouteEntries(input: RouteEntriesInput): RouteEntry[] {
  const tokenByRoute = buildTokenByRoute(input.tokens);
  const pipelinesByRoute = groupPipelinesByRoute(
    input.jobs,
    input.serviceName,
    input.jobStatusMap,
  );

  const seen = new Set<string>();
  const entries: RouteEntry[] = [];

  for (const route of input.routes) {
    seen.add(route);
    entries.push(toRouteEntry(route, tokenByRoute, pipelinesByRoute));
  }
  for (const route of pipelinesByRoute.keys()) {
    if (!seen.has(route)) {
      entries.push(toRouteEntry(route, tokenByRoute, pipelinesByRoute));
    }
  }

  return entries;
}

function buildSnippet(
  route: string,
  baseUrl: string,
  apiKeyHint: string | undefined,
): string {
  const endpoint = route ? `/ingest/${route}` : "/ingest/{route}";
  const lines: string[] = [];
  if (apiKeyHint) {
    lines.push(
      `# Token: ${apiKeyHint}… — replace YOUR_API_KEY with the full key`,
    );
  }
  lines.push(`curl -X POST ${baseUrl}${endpoint} \\`);
  lines.push(`  -H "Content-Type: application/json" \\`);
  if (apiKeyHint) {
    lines.push(`  -H "x-api-key: YOUR_API_KEY" \\`);
  }
  lines.push(`  -d '{"key": "value"}'`);
  return lines.join("\n");
}

function RouteBlock({
  entry,
  baseUrl,
  onCopy,
  onJobClick,
}: {
  entry: RouteEntry;
  baseUrl: string;
  onCopy: (text: string) => void;
  onJobClick: (jobName: string) => void;
}) {
  const snippet = buildSnippet(entry.route, baseUrl, entry.apiKeyHint);
  const endpoint = entry.route ? `/ingest/${entry.route}` : "/ingest/{route}";

  return (
    <RouteCard>
      <RouteHeader>
        <RouteLabel>{entry.route || "(default)"}</RouteLabel>
        <CopySnippetButton onClick={() => onCopy(snippet)}>
          Copy
        </CopySnippetButton>
      </RouteHeader>
      <SnippetBody>
        <EndpointLabel>POST {endpoint}</EndpointLabel>
        <SnippetCode>{snippet}</SnippetCode>
      </SnippetBody>
      {entry.pipelines.length > 0 && (
        <PipelineList>
          {entry.pipelines.map(({ jobName, running }) => (
            <PipelineRow key={jobName} onClick={() => onJobClick(jobName)}>
              <StatusDot $running={running} />
              <PipelineName>{jobName}</PipelineName>
              {running ? (
                <BadgeConfirmationSubtle>Running</BadgeConfirmationSubtle>
              ) : (
                <BadgeNeutralSubtle>Stopped</BadgeNeutralSubtle>
              )}
            </PipelineRow>
          ))}
        </PipelineList>
      )}
    </RouteCard>
  );
}

export function IngestPipelinesSection({
  serviceName,
  port,
  jobs,
  jobStatusMap,
  tokens,
  routes,
  onCopy,
  onJobClick,
}: IngestPipelinesSectionProps) {
  const baseUrl = useMemo(() => getIngestBaseUrl(port), [port]);
  const entries = useMemo(
    () =>
      buildRouteEntries({ routes, jobs, serviceName, jobStatusMap, tokens }),
    [routes, jobs, serviceName, jobStatusMap, tokens],
  );

  return (
    <Section>
      <SectionTitle>Ingest Endpoints</SectionTitle>
      {entries.length > 0 ? (
        <RouteList>
          {entries.map((entry) => (
            <RouteBlock
              key={entry.route || "__default__"}
              entry={entry}
              baseUrl={baseUrl}
              onCopy={onCopy}
              onJobClick={onJobClick}
            />
          ))}
        </RouteList>
      ) : (
        <EmptyState style={{ padding: 24 }}>
          No routes configured. Add routes to this service to see ingest
          endpoints and curl snippets here.
        </EmptyState>
      )}
    </Section>
  );
}

const RouteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RouteCard = styled.div`
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 4px;
  overflow: hidden;
`;

const RouteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: ${(p) => p.theme.tableTheadCellBackground};
  border-bottom: 1px solid ${(p) => p.theme.cards1Border};
`;

const RouteLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.semanticTextHeading};
`;

const SnippetBody = styled.div`
  padding: 10px 12px;
`;

const EndpointLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: ${(p) => p.theme.semanticTextHeading};
  margin-bottom: 8px;
`;

const SnippetCode = styled.pre`
  margin: 0;
  padding: 10px;
  font-size: 12px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: ${(p) => p.theme.semanticTextBody};
  background: ${(p) => p.theme.tableTheadCellBackground};
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.5;
`;

const PipelineList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${(p) => p.theme.cards1Border};
`;

const PipelineRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: none;
  border: none;
  border-bottom: 1px solid ${(p) => p.theme.cards1Border};
  cursor: pointer;
  font-size: 12px;
  color: ${(p) => p.theme.semanticTextBody};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${(p) => p.theme.tableTbodyCellHoverBackground};
    color: ${(p) => p.theme.linkColor};
  }
`;

const StatusDot = styled.span<{ $running: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) =>
    p.$running
      ? p.theme.badgeConfirmSubtleColor
      : p.theme.badgeNeutralSubtleColor};
`;

const PipelineName = styled.span`
  font-weight: 500;
`;

const CopySnippetButton = styled.button`
  background: ${(p) => p.theme.cards1Background};
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  color: ${(p) => p.theme.semanticTextLight};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${(p) => p.theme.tableTheadCellBackground};
  }
`;
